import { useReducedMotion } from "@src/packages/moon-ui/hooks/useReducedMotion";
import { useEffect, useRef } from "react";
import {
  View,
  Text,
  Pressable,
  Animated,
  Easing,
  type ViewStyle,
} from "react-native";
import { Users, Heart, Clock, Check, X } from "lucide-react-native";
import {
  cardSurface,
  CardSubtitleText,
  CardTitleText,
  CompactActionButton,
  IconButton,
  StatusBadge,
  TextActionButton,
  semanticColors,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { EMBER } from "../../../../onboarding/theme";
import { useYouScreen } from "../YouScreenContext";
import { usePartnerInvitation } from "@src/packages/patients/hooks/usePartnerInvite";
import { useCarePersons } from "@src/packages/patients/hooks/useCarePersons";
import { useDismissible } from "@src/packages/homepage/utils/useDismissible";

/**
 * You and your person, side by side: a gentle visual metaphor for
 * partnership, drawn in the colour of the card title beneath it.
 */
function PartnerVisual({ compact = false }: { compact?: boolean }) {
  const iconSize = compact ? 12 : 14;
  const iconColor = semanticColors.typography.primary;
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        marginBottom: compact ? 10 : 14,
      }}
    >
      <Heart size={iconSize} color={iconColor} strokeWidth={1.5} />
      <Users size={iconSize} color={iconColor} strokeWidth={1.5} />
    </View>
  );
}

/** Soft pulsing dot for pending state */
function PulsingDot() {
  const opacity = useRef(new Animated.Value(0.4)).current;

  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (reducedMotion) return;
    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 1,
          duration: 1000,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 0.4,
          duration: 1000,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    pulse.start();
    return () => pulse.stop();
  }, [opacity, reducedMotion]);

  return (
    <Animated.View
      style={{
        width: 6,
        height: 6,
        borderRadius: 3,
        backgroundColor: EMBER.colors.warm,
        opacity,
      }}
    />
  );
}

export default function PartnerCard({
  compact = false,
}: {
  compact?: boolean;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("PartnerCard");
  const { dispatch, patient } = useYouScreen();
  const { data: invitation } = usePartnerInvitation();
  const { partnerName: linkedPartnerName } = useCarePersons();
  const { isDismissed, dismiss } = useDismissible("you/partner-invite-card");

  const hasPartner = !!patient?.partnerConnected;
  const isPending = invitation?.status === "pending";
  const partnerName = invitation?.inviteeFirstName;

  // Partner state belongs in the visual/status treatment below, not in a
  // one-off orange card border. Reuse the standard You glass-card surface.
  const cardStyle = {
    ...cardSurface(),
    overflow: "hidden",
    padding: compact ? 14 : 18,
    ...(compact ? { minHeight: 198 } : null),
  } as ViewStyle;

  // The invite surface is an optional prompt. A patient can still add a
  // partner later from Connections & Sharing, so retain its dismissal across
  // devices instead of treating the card itself as a required step.
  if (isDismissed && !hasPartner && !isPending) {
    return null;
  }

  // ── Linked state ──
  if (hasPartner) {
    return (
      <View style={{ marginBottom: 16 }}>
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(
                linkedPartnerName
                  ? t("you.partner_card.linked_with_name", {
                      params: { name: linkedPartnerName },
                    })
                  : t("you.partner_card.linked_with_partner"),
              ),
              "partner_card_linked",
            );
            dispatch({
              type: "SHOW_DATA_SHARING_MODAL",
              connectionType: "partner",
            });
          }}
          style={({ pressed }) => [
            cardStyle,
            pressed
              ? { backgroundColor: semanticColors.action.quiet.background }
              : null,
          ]}
        >
          <PartnerVisual />

          <CardTitleText>
            {linkedPartnerName
              ? t("you.partner_card.linked_with_name", {
                  params: { name: linkedPartnerName },
                })
              : t("you.partner_card.linked_with_partner")}
          </CardTitleText>

          <CardSubtitleText style={{ marginBottom: 12 }}>
            {t("you.partner_card.linked_blurb")}
          </CardSubtitleText>

          <View style={{ alignSelf: "flex-start" }}>
            <StatusBadge
              label={t("you.partner_card.connected")}
              tone="green"
              icon={Check}
            />
          </View>
        </Pressable>
      </View>
    );
  }

  // ── Pending state ──
  if (isPending) {
    return (
      <View style={{ marginBottom: 16 }}>
        <View style={cardStyle}>
          <PartnerVisual />

          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
              marginBottom: 4,
            }}
          >
            <CardTitleText>
              {partnerName
                ? t("you.partner_card.waiting_for", {
                    params: { name: partnerName },
                  })
                : t("you.partner_card.waiting_for_partner")}
            </CardTitleText>
            <PulsingDot />
          </View>

          <CardSubtitleText style={{ marginBottom: 12 }}>
            {partnerName
              ? t("you.partner_card.we_sent_invite", {
                  params: { name: partnerName },
                })
              : t("you.partner_card.we_sent_invite_generic")}
          </CardSubtitleText>

          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 12,
            }}
          >
            <StatusBadge
              label={t("you.partner_card.pending")}
              tone="gold"
              icon={Clock}
            />

            <TextActionButton
              label={t("you.partner_card.resend")}
              action="you.partner_card.resend"
              tone="tertiary"
              size="sm"
              onPress={() => {
                dispatch({
                  type: "SET_PARTNER_INVITE_NAME",
                  name: partnerName ?? "",
                });
                dispatch({
                  type: "SET_PARTNER_INVITE_PHONE",
                  phone: invitation?.inviteePhone ?? "",
                });
                dispatch({ type: "SHOW_MODAL", modal: "partner-invite" });
              }}
              style={{ paddingHorizontal: 0 }}
            />

            <TextActionButton
              label={t("you.partner_card.manage_invitation")}
              action="you.partner_card.manage_invitation"
              tone="tertiary"
              size="sm"
              onPress={() => {
                dispatch({
                  type: "SHOW_DATA_SHARING_MODAL",
                  connectionType: "partner",
                });
              }}
              style={{ paddingHorizontal: 0 }}
            />
          </View>
        </View>
      </View>
    );
  }

  // ── Default invite state ──
  return (
    <View
      style={[{ marginBottom: 16 }, compact ? { flex: 1, minWidth: 0 } : null]}
    >
      <View style={cardStyle}>
        <View
          style={{
            position: "absolute",
            top: compact ? 8 : 10,
            right: compact ? 8 : 10,
          }}
        >
          <IconButton
            accessibilityLabel={t("you.partner_card.dismiss")}
            onPress={dismiss}
            size="sm"
            surface="soft"
            backgroundColor={semanticColors.surface.muted}
            pressedBackgroundColor={semanticColors.overlay.pressed}
          >
            <X size={14} color={EMBER.colors.textLight} strokeWidth={1.8} />
          </IconButton>
        </View>

        <PartnerVisual compact={compact} />

        <CardTitleText
          style={{ marginBottom: 4 }}
          numberOfLines={compact ? 2 : undefined}
        >
          {t("you.partner_card.doing_this_with_someone")}
        </CardTitleText>

        <Text
          className="font-dm-sans"
          style={{
            flex: compact ? 1 : undefined,
            fontSize: compact ? 12 : 13,
            fontWeight: "400",
            color: EMBER.colors.textLight,
            lineHeight: compact ? 16 : 18,
            marginBottom: compact ? 12 : 14,
          }}
          numberOfLines={compact ? 3 : undefined}
        >
          {t(
            compact
              ? "you.partner_card.compact_blurb"
              : "you.partner_card.invite_blurb",
          )}
        </Text>

        <CompactActionButton
          // The half-width card already asks "Doing this with someone?", so
          // the short label fits there without truncating.
          label={t(
            compact
              ? "you.partner_card.invite"
              : "you.partner_card.invite_partner",
          )}
          accessibilityLabel={t("you.partner_card.invite_partner")}
          action="you.partner_card.invite_partner"
          onPress={() =>
            dispatch({ type: "SHOW_MODAL", modal: "partner-invite" })
          }
          variant="primary"
          leading={
            <Users
              size={13}
              color={semanticColors.action.tonal.foreground}
              strokeWidth={1.9}
            />
          }
          numberOfLines={1}
          style={{ alignSelf: compact ? "stretch" : "flex-start" }}
          textStyle={compact ? { fontSize: 12 } : undefined}
        />
      </View>
    </View>
  );
}
